import { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './lib/supabase';
import { LandingPage, type AppPage } from './LandingPage';
import { FeaturesPage } from './FeaturesPage';
import { HowItWorksPage } from './HowItWorksPage';
import { PricingPage } from './PricingPage';
import { TermsPage } from './TermsPage';
import { PrivacyPage } from './PrivacyPage';
import { AuthModal } from './AuthModal';
import { Sidebar, type DashView } from './components/Sidebar';
import { DashboardMain } from './components/DashboardMain';
import { SettingsPage } from './components/SettingsPage';
import { StylePage } from './components/StylePage';
import { InsightsPage } from './components/InsightsPage';
import { KnowledgePage } from './components/KnowledgePage';
import { IntegrationsPage } from './components/IntegrationsPage';
import { ChangelogPage } from './ChangelogPage';

type AppState = 'loading' | 'unauthenticated' | 'authenticated' | 'password_reset';

function PasswordResetScreen() {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    if (password.length < 8) { setErrorMsg('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setErrorMsg('Passwords do not match.'); return; }
    setStatus('loading');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setErrorMsg(error.message);
      setStatus('error');
    } else {
      setStatus('done');
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-page)' }}>
      <div style={{ width: 360, padding: '40px 32px', background: 'var(--bg-surface)', borderRadius: 16, border: '1px solid var(--border-1)' }}>
        <h2 style={{ font: '600 20px/1.2 var(--font-sans)', color: 'var(--fg-1)', margin: '0 0 8px' }}>Set new password</h2>
        <p style={{ font: '400 14px/1.5 var(--font-sans)', color: 'var(--fg-3)', margin: '0 0 24px' }}>Choose a strong password for your account.</p>
        {status === 'done' ? (
          <p style={{ font: '400 14px/1.5 var(--font-sans)', color: 'var(--fg-2)', textAlign: 'center' }}>
            Password updated. Taking you to your dashboard…
          </p>
        ) : (
          <>
            <input
              type="password"
              placeholder="New password (8+ chars)"
              value={password}
              onChange={e => { setPassword(e.target.value); setErrorMsg(''); }}
              style={{ width: '100%', marginBottom: 10, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--bg-sunken)', color: 'var(--fg-1)', font: '400 14px var(--font-sans)', boxSizing: 'border-box' }}
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={e => { setConfirm(e.target.value); setErrorMsg(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              style={{ width: '100%', marginBottom: errorMsg ? 8 : 16, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-1)', background: 'var(--bg-sunken)', color: 'var(--fg-1)', font: '400 14px var(--font-sans)', boxSizing: 'border-box' }}
            />
            {errorMsg && <p style={{ font: '400 13px/1.4 var(--font-sans)', color: 'var(--danger)', margin: '0 0 12px' }}>{errorMsg}</p>}
            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={status === 'loading'}
              style={{ width: '100%' }}
            >
              {status === 'loading' ? 'Updating…' : 'Update password →'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const DASH_PATHS: Record<string, DashView> = {
  '/style':        'style',
  '/insights':     'insights',
  '/knowledge':    'knowledge',
  '/integrations': 'integrations',
  '/profile':      'profile',
  '/subscription': 'subscription',
  '/settings':     'settings',
};

export interface DraftSession {
  id: string;
  deal_state: string;
  platform: string;
  context_synced: boolean;
  lead_responded: boolean;
  created_at: string;
  leads: { name: string; company: string } | null;
  draft_history: {
    ai_drafts: { responses: { tone: string; text: string }[]; rationale: string; suggested_next_steps: string[] } | null;
    options_used: Record<string, string>;
    selected_draft_index: number | null;
    is_sent: boolean | null;
  }[];
}

function Dashboard({ session }: { session: Session }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [sessions, setSessions] = useState<DraftSession[]>([]);
  const [userProfile, setUserProfile] = useState<{ industry?: string; role?: string; communication_channels?: string[]; sales_persona?: string } | null>(null);

  const dashView: DashView = DASH_PATHS[location.pathname] ?? 'responses';

  function handleNavigate(view: DashView) {
    navigate(view === 'responses' ? '/' : `/${view}`);
  }

  useEffect(() => {
    const fetchSessions = () => {
      supabase
        .from('interactions')
        .select('id, deal_state, platform, context_synced, lead_responded, created_at, leads(name, company), draft_history(ai_drafts, options_used, selected_draft_index, is_sent)')
        .order('created_at', { ascending: false })
        .limit(50)
        .then(({ data }) => setSessions((data as unknown as DraftSession[]) ?? []));
    };

    const fetchProfile = () => {
      supabase
        .from('profiles')
        .select('industry, role, communication_channels, sales_persona, subscription_status')
        .eq('id', session.user.id)
        .single()
        .then(({ data }) => {
          if (data) {
            setUserProfile(data);
          }
        });
    };

    fetchSessions();
    fetchProfile();

    const interactionsChannel = supabase
      .channel('dashboard-interactions')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'interactions',
          filter: `user_id=eq.${session.user.id}`
        },
        () => {
          fetchSessions();
        }
      )
      .subscribe();

    const profilesChannel = supabase
      .channel('dashboard-profiles')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${session.user.id}`
        },
        () => {
          fetchProfile();
          // Covers profile changes that don't originate from this page (e.g. a
          // Stripe webhook flipping subscription_status) — nudge the extension
          // to re-cache so its plan badge and Pro gates stay aligned.
          try { window.postMessage({ type: 'salifly-ext-profile-updated' }, window.location.origin); } catch { /* ignore */ }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(interactionsChannel);
      supabase.removeChannel(profilesChannel);
    };
  }, [session.user.id]);

  return (
    <div className="website-layout">
      <Sidebar
        user={session.user}
        activeView={dashView}
        onNavigate={handleNavigate}
        onSignOut={() => supabase.auth.signOut()}
      />
      {dashView === 'responses' && (
        <DashboardMain
          sessions={sessions}
          query={searchQuery}
          onQuery={setSearchQuery}
          onDelete={async (id) => {
            await supabase.from('interactions').delete().eq('id', id);
            setSessions(prev => prev.filter(s => s.id !== id));
          }}
        />
      )}
      {dashView === 'style' && (
        <StylePage userId={session.user.id} userProfile={userProfile} sessions={sessions} />
      )}
      {dashView === 'insights' && (
        <InsightsPage userId={session.user.id} />
      )}
      {dashView === 'knowledge' && (
        <KnowledgePage userId={session.user.id} />
      )}
      {dashView === 'integrations' && (
        <IntegrationsPage userId={session.user.id} />
      )}
      {(dashView === 'profile' || dashView === 'subscription' || dashView === 'settings') && (
        <SettingsPage user={session.user} activeTab={dashView} />
      )}
    </div>
  );
}

function App() {
  const [appState, setAppState] = useState<AppState>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalView, setModalView] = useState<'signup' | 'login'>('signup');
  const [modalError, setModalError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let link: HTMLLinkElement | null = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    const cleanPath = location.pathname === '/' ? '' : location.pathname.replace(/\/$/, '');
    link.setAttribute('href', `https://salifly.com${cleanPath}`);
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authParam = params.get('auth');
    if (authParam === 'signup' || authParam === 'login' || authParam === 'true') {
      setModalView(authParam === 'login' ? 'login' : 'signup');
      setModalOpen(true);
      
      const newParams = new URLSearchParams(location.search);
      newParams.delete('auth');
      const search = newParams.toString();
      navigate({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAppState(session ? 'authenticated' : 'unauthenticated');
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSession(session);
        setAppState('password_reset');
        return;
      }
      if (event === 'SIGNED_OUT') {
        // Persist an explicit-signout marker so the extension's background
        // worker knows this was a deliberate sign-out (and should log the
        // extension out too) rather than a stale/expired session copy it
        // should overwrite with its own session on the next page load.
        try { localStorage.setItem('salifly-signout-marker', '1'); } catch { /* ignore */ }
        // Signal the extension's authSync content script immediately so it
        // can clear the session from chrome.storage.local without waiting for
        // the 2-second poll — covers the "delete account then navigate away" case.
        try {
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'salifly-ext-signout',
            newValue: '1',
            storageArea: localStorage,
          }));
          window.postMessage({ type: 'salifly-ext-signout' }, '*');
        } catch { /* ignore */ }
      } else if ((event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') && session) {
        try { localStorage.removeItem('salifly-signout-marker'); } catch { /* ignore */ }
        // Block new users who clicked "Log in" (instead of "Sign up") from
        // having a Google OAuth account silently created for them.
        let oauthMode: string | null = null;
        try {
          oauthMode = localStorage.getItem('salifly_oauth_mode');
        } catch { /* ignore */ }

        if (oauthMode) {
          try { localStorage.removeItem('salifly_oauth_mode'); } catch { /* ignore */ }

          if (oauthMode === 'login') {
            const createdAt = new Date(session.user.created_at).getTime();
            if (Number.isFinite(createdAt) && Date.now() - createdAt < 10_000) {
              void supabase.auth.signOut().then(() => {
                setModalError('No Salifly account found for that Google account. Please sign up first.');
                setModalView('signup');
                setModalOpen(true);
              });
              return;
            }
          }
        }


        try {
          window.dispatchEvent(new StorageEvent('storage', {
            key: 'salifly-ext-signin',
            newValue: JSON.stringify(session),
            storageArea: localStorage,
          }));
          window.postMessage({ type: 'salifly-ext-signin', session }, '*');
        } catch { /* ignore */ }
      }
      setSession(session);
      setAppState(session ? 'authenticated' : 'unauthenticated');
    });

    // The extension's background worker pushes its session into this page when
    // the website's own copy is missing or stale (extension logged in, website
    // logged out). Adopt it live so the user lands authenticated without a reload.
    const onExtSessionPush = (event: MessageEvent) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const data = event.data as { type?: string; session?: { access_token?: unknown; refresh_token?: unknown } } | null;
      if (data?.type !== 'salifly-ext-session-push' || !data.session) return;
      const { access_token, refresh_token } = data.session;
      if (
        typeof access_token !== 'string' || access_token.length < 20 ||
        typeof refresh_token !== 'string' || refresh_token.length === 0
      ) return;
      supabase.auth.getSession().then(({ data: { session: current } }) => {
        if (current?.access_token === access_token) return;
        void supabase.auth.setSession({ access_token, refresh_token }).catch(() => { /* invalid push — stay logged out */ });
      });
    };
    window.addEventListener('message', onExtSessionPush);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('message', onExtSessionPush);
    };
  }, []);

  if (appState === 'password_reset') {
    return <PasswordResetScreen />;
  }

  if (appState === 'loading') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-page)' }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--border-1)', borderTopColor: 'var(--accent)', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  if (appState === 'authenticated' && session) {
    const pagePropsAuth = {
      onNavigate: (p: AppPage) => navigate(p === 'home' ? '/' : `/${p}`),
      onGetStarted: () => navigate('/'),
      onLogin: () => navigate('/'),
      isAuthenticated: true
    };
    return (
      <Routes>
        <Route path="/features" element={<FeaturesPage {...pagePropsAuth} />} />
        <Route path="/how-it-works" element={<HowItWorksPage {...pagePropsAuth} />} />
        <Route path="/pricing" element={<PricingPage {...pagePropsAuth} />} />
        <Route path="/terms" element={<TermsPage {...pagePropsAuth} />} />
        <Route path="/privacy" element={<PrivacyPage {...pagePropsAuth} />} />
        <Route path="/changelog" element={<ChangelogPage {...pagePropsAuth} />} />
        <Route path="*" element={<Dashboard session={session} />} />
      </Routes>
    );
  }

  const onNavigate = (p: AppPage) => navigate(`/${p === 'home' ? '' : p}`);
  const pageProps = {
    onNavigate,
    onGetStarted: () => { setModalView('signup'); setModalOpen(true); },
    onLogin: () => { setModalView('login'); setModalOpen(true); }
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<LandingPage {...pageProps} />} />
        <Route path="/features" element={<FeaturesPage {...pageProps} />} />
        <Route path="/how-it-works" element={<HowItWorksPage {...pageProps} />} />
        <Route path="/pricing" element={<PricingPage {...pageProps} />} />
        <Route path="/terms" element={<TermsPage {...pageProps} />} />
        <Route path="/privacy" element={<PrivacyPage {...pageProps} />} />
        <Route path="/changelog" element={<ChangelogPage {...pageProps} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <AuthModal
        open={modalOpen}
        view={modalView}
        initialError={modalError}
        onClose={() => { setModalOpen(false); setModalError(null); }}
        onNavigate={onNavigate}
      />
    </>
  );
}

export default App;
